import type { EtiquetaDaOpcao, OpcaoDeSelecao } from '@/componentes/Campo';
import { formatarData, formatarDecimal, formatarReais } from '@/lib/formato';

import { ROTULO_DA_CATEGORIA } from './saldo';

/**
 * O AUTOPREENCHIMENTO DO ESTOQUE E DAS COMPRAS (pedido do dono, 31/10/2026):
 * *"facilitar e agilizar ao máximo a vida de quem vai usar o sistema"*, e as
 * melhorias do formulário de combustível levadas aos outros.
 *
 * O que o sistema já sabe não se pergunta. Cada função daqui responde UMA
 * pergunta que o formulário fazia à pessoa, e devolve junto a frase de onde
 * veio a resposta: a ajuda do campo diz "Último custo: R$ 12,40 em
 * 20/10/2026", e quem lança confere sem abrir nada. Valor que o sistema
 * escreveu sem dizer de onde ninguém confere — e ninguém confere justamente o
 * que o sistema errou sozinho.
 *
 * Nada aqui é regra: o servidor continua recusando o que recusava. Uma
 * sugestão errada se troca no campo, e a resposta passa a ser da pessoa.
 *
 * Puras, para o teste provar cada escolha sem desenhar a tela
 * (`preenchimento.test.ts`).
 */

/** O valor que o sistema propõe para um campo, e a frase de onde ele veio (vai na ajuda). */
export interface Sugestao {
  readonly valor: string;
  readonly ajuda: string;
}

/** O que muda a escolha do depósito: de onde a peça sai ou onde ela entra. */
export type TipoDaEscolhaDoDeposito = 'entrada' | 'saida' | 'devolucao' | 'transferencia' | 'contagem' | 'pedido';

/** A posição do item num local, como o saldo a devolve (`SaldoDaApi`). */
export interface PosicaoDoItem {
  readonly produtoId: string;
  readonly localTipo: 'deposito' | 'tanque';
  readonly localId: string;
  readonly fisico: number;
  readonly disponivel: number;
}

function medida(valor: number, unidade: string | undefined): string {
  return unidade ? `${formatarDecimal(valor, 1)} ${unidade}` : formatarDecimal(valor, 1);
}

/**
 * O DEPÓSITO que o formulário já traz escolhido.
 *
 * - Um depósito só na empresa: é ele, e pronto.
 * - Na SAÍDA e na TRANSFERÊNCIA a peça sai de onde ela ESTÁ: o último
 *   depósito usado, se ele tem o item; senão, o que tem mais disponível. Um
 *   depósito sem o item seria uma recusa do servidor a caminho.
 * - Na ENTRADA, na DEVOLUÇÃO, na CONTAGEM e no PEDIDO a peça vai para onde ela
 *   MORA: o depósito em que o item já tem posição (o último usado, se for um
 *   deles); sem posição nenhuma, o último usado.
 *
 * Sem resposta honesta, `null`: o campo fica à vista, em branco.
 */
export function depositoSugerido(entrada: {
  readonly tipo: TipoDaEscolhaDoDeposito;
  readonly produtoId: string;
  readonly depositos: readonly { readonly id: string; readonly nome: string }[];
  readonly saldos: readonly PosicaoDoItem[];
  /** O último depósito que esta pessoa usou neste navegador, ou `null`. */
  readonly ultimo: string | null;
  readonly unidade?: string;
}): Sugestao | null {
  const { depositos, tipo, produtoId, unidade } = entrada;
  if (depositos.length === 0) return null;
  const unico = depositos.length === 1 ? depositos[0] : undefined;
  if (unico) return { valor: unico.id, ajuda: 'Único depósito cadastrado.' };

  const existe = new Set(depositos.map((d) => d.id));
  const ultimo = entrada.ultimo !== null && existe.has(entrada.ultimo) ? entrada.ultimo : null;
  const posicoes = produtoId === ''
    ? []
    : entrada.saldos.filter((s) => s.localTipo === 'deposito' && s.produtoId === produtoId && existe.has(s.localId));

  if (tipo === 'saida' || tipo === 'transferencia') {
    const comSaldo = posicoes.filter((s) => s.disponivel > 0).sort((a, b) => b.disponivel - a.disponivel);
    const doUltimo = comSaldo.find((s) => s.localId === ultimo);
    if (doUltimo) return { valor: doUltimo.localId, ajuda: `Último depósito usado. Disponível: ${medida(doUltimo.disponivel, unidade)}.` };
    const maior = comSaldo[0];
    if (maior) {
      return {
        valor: maior.localId,
        ajuda:
          comSaldo.length === 1
            ? `Único depósito com o item: ${medida(maior.disponivel, unidade)} disponíveis.`
            : `O depósito com mais saldo do item: ${medida(maior.disponivel, unidade)} disponíveis.`,
      };
    }
    return ultimo === null ? null : { valor: ultimo, ajuda: 'Último depósito usado. O item não tem saldo disponível em nenhum depósito.' };
  }

  if (posicoes.length > 0) {
    const doUltimo = posicoes.find((s) => s.localId === ultimo);
    if (doUltimo) return { valor: doUltimo.localId, ajuda: 'Último depósito usado, onde o item já está.' };
    const [maior] = [...posicoes].sort((a, b) => b.fisico - a.fisico);
    if (maior) {
      return {
        valor: maior.localId,
        ajuda: posicoes.length === 1 ? 'O depósito onde o item já está.' : 'O depósito onde o item tem mais saldo.',
      };
    }
  }
  return ultimo === null ? null : { valor: ultimo, ajuda: 'Último depósito usado.' };
}

/** Uma linha do livro, só com o que a última entrada precisa. */
export interface MovimentoDaUltimaEntrada {
  readonly tipo: string;
  readonly custoUnitario: number | null;
  readonly ocorridoEm: string;
  readonly fornecedorId?: string | null;
  readonly fornecedor?: string | null;
}

export interface UltimaEntrada {
  /** O custo por unidade do ITEM (o banco já converteu a embalagem) e quando. */
  readonly custo: { readonly valor: number; readonly em: string } | null;
  readonly fornecedor: { readonly id: string; readonly nome: string; readonly em: string } | null;
}

/**
 * O que a última entrada do item diz: o custo e o fornecedor. O livro vem do
 * mais novo para o mais velho (`GET /estoque/movimentos`). Cada um é o da
 * entrada mais recente que o TEM: a nota sem custo não apaga o custo de antes.
 */
export function ultimaEntrada(movimentos: readonly MovimentoDaUltimaEntrada[]): UltimaEntrada {
  const entradas = movimentos.filter((m) => m.tipo === 'entrada');
  const comCusto = entradas.find((m) => m.custoUnitario !== null && m.custoUnitario > 0);
  const comFornecedor = entradas.find((m) => m.fornecedorId);
  return {
    custo: comCusto && comCusto.custoUnitario !== null ? { valor: comCusto.custoUnitario, em: comCusto.ocorridoEm } : null,
    fornecedor:
      comFornecedor?.fornecedorId
        ? { id: comFornecedor.fornecedorId, nome: comFornecedor.fornecedor ?? '', em: comFornecedor.ocorridoEm }
        : null,
  };
}

/**
 * O custo que o campo já traz: o da última entrada, na unidade em que a
 * pessoa vai digitar — na caixa de 12, doze vezes o da unidade. Duas casas,
 * com vírgula, como se digita.
 */
export function custoSugerido(
  custo: UltimaEntrada['custo'],
  unidadeDoItem: string,
  embalagem: { readonly unidade: string; readonly fator: number } | null,
): Sugestao | null {
  if (custo === null) return null;
  const fator = embalagem?.fator ?? 1;
  const valor = Math.round(custo.valor * fator * 100) / 100;
  const quando = formatarData(new Date(custo.em));
  return {
    valor: formatarDecimal(valor, 2),
    ajuda: embalagem
      ? `Último custo: ${formatarReais(custo.valor)} por ${unidadeDoItem} em ${quando}, ${formatarReais(valor)} na ${embalagem.unidade}.`
      : `Último custo: ${formatarReais(custo.valor)} em ${quando}.`,
  };
}

/** O total que acompanha a digitação: quantidade × preço, ou `null` enquanto falta um dos dois. */
export function totalDaLinha(quantidade: number | null, preco: number | null): number | null {
  if (quantidade === null || preco === null || quantidade <= 0 || preco < 0) return null;
  return Math.round(quantidade * preco * 100) / 100;
}

/**
 * A CONTAGEM EM FRASE, a cada tecla: o que o livro diz e o que vira ajuste.
 * Não é julgamento — contar menos do que o livro é o motivo de contar.
 */
export function fraseDaContagem(fisico: number | null, contado: number | null, unidade: string): string | null {
  if (fisico === null) return null;
  const noLivro = `No livro: ${medida(fisico, unidade)}.`;
  if (contado === null || contado < 0) return noLivro;
  const diferenca = Math.round((contado - fisico) * 1000) / 1000;
  if (diferenca === 0) return `${noLivro} Bate com o livro: nenhum ajuste.`;
  return diferenca > 0
    ? `${noLivro} Vira ajuste de entrada de ${medida(diferenca, unidade)}.`
    : `${noLivro} Vira ajuste de saída de ${medida(-diferenca, unidade)}.`;
}

/**
 * A unidade que o cadastro de item já traz pela categoria: lubrificante se
 * conta em litro; o resto, por unidade. É só o começo — o campo continua lá.
 */
export function unidadeDaCategoria(categoria: string): string {
  return categoria === 'lubrificante' ? 'L' : 'un';
}

/**
 * O PRÓXIMO NÚMERO DE PEDIDO, depois do maior que a empresa já usou: "PC-0012"
 * vira "PC-0013", com os mesmos zeros à esquerda. Sem número que termine em
 * algarismo, `null`: o campo fica em branco.
 */
export function proximoNumeroDePedido(numeros: readonly string[]): string | null {
  let melhor: { readonly prefixo: string; readonly digitos: string; readonly valor: bigint } | null = null;
  for (const numero of numeros) {
    const partes = /^(.*?)(\d+)$/.exec(numero.trim());
    if (!partes) continue;
    const prefixo = partes[1] ?? '';
    const digitos = partes[2] ?? '';
    const valor = BigInt(digitos);
    if (melhor === null || valor > melhor.valor) melhor = { prefixo, digitos, valor };
  }
  if (melhor === null) return null;
  return `${melhor.prefixo}${(melhor.valor + 1n).toString().padStart(melhor.digitos.length, '0')}`;
}

/**
 * O prazo que o fornecedor deu da última vez: o do pedido mais novo dele que
 * tem prazo. A lista vem do mais novo para o mais velho (`GET /compras/pedidos`).
 */
export function prazoDoFornecedor(
  pedidos: readonly { readonly numero: string; readonly fornecedor: string; readonly prazoDias: number | null }[],
  fornecedor: string,
): { readonly dias: number; readonly pedido: string } | null {
  const nome = fornecedor.trim().toLocaleLowerCase('pt-BR');
  if (nome === '') return null;
  const achado = pedidos.find((p) => p.fornecedor.trim().toLocaleLowerCase('pt-BR') === nome && p.prazoDias !== null);
  return achado && achado.prazoDias !== null ? { dias: achado.prazoDias, pedido: achado.numero } : null;
}

/**
 * O ITEM DO ESTOQUE NA LISTA DE ESCOLHA: o nome no rótulo, e o código e a
 * unidade em etiqueta (pedido do dono, 31/10/2026: os identificadores que se
 * conferem de olho, cada um num tom fixo). O código é o azul de informação,
 * como o código interno da máquina; a unidade é atributo, neutra. Os dois
 * entram na busca da lista.
 */
export function opcaoDoItem(item: {
  readonly id: string;
  readonly codigo: string;
  readonly nome: string;
  readonly unidade: string;
  readonly categoria?: string;
}): OpcaoDeSelecao {
  const etiquetas: EtiquetaDaOpcao[] = [
    { texto: item.codigo, tom: 'informacao', titulo: 'Código' },
    { texto: item.unidade, tom: 'neutro', titulo: 'Unidade' },
  ];
  const categoria = item.categoria ? (ROTULO_DA_CATEGORIA[item.categoria] ?? item.categoria) : undefined;
  return { valor: item.id, rotulo: item.nome, etiquetas, ...(categoria ? { detalhe: categoria } : {}) };
}

/* ------------------------------------------------- o último que se usou */

/** O pedaço do `Storage` que a lembrança usa. No teste, um `Map` resolve. */
export interface Armazem {
  getItem(chave: string): string | null;
  setItem(chave: string, valor: string): void;
}

/**
 * A chave leva a PESSOA: o almoxarife do pátio e o da obra usam depósitos
 * diferentes no mesmo navegador do escritório. O id da pessoa já é da empresa.
 */
export function chaveDoUltimo(usuarioId: string, campo: string): string {
  return `ct-frotas:ultimo:${usuarioId}:${campo}`;
}

export function lembrado(armazem: Armazem | null, chave: string): string | null {
  try {
    const valor = armazem?.getItem(chave) ?? null;
    return valor === null || valor === '' ? null : valor;
  } catch {
    return null;
  }
}

export function lembrar(armazem: Armazem | null, chave: string, valor: string): void {
  if (valor === '') return;
  try {
    armazem?.setItem(chave, valor);
  } catch {
    /* Armazenamento cheio ou bloqueado: o formulário segue sem lembrança. */
  }
}

export function armazemDoNavegador(): Armazem | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
